/** @file Keyframe form model: one selected key in command units, its value kind, the keys' shared curve. */
import { KEY_CHANNELS } from '@pose2d/commands/_shared/keyChannels';
import type { Curve, Doc } from '@pose2d/format/types';
import type { KeyRef } from '../../stores/types';
import { buildCurveTrack } from '../curves/buildCurveTrack';
import { toCurveRef } from '../curves/toCurveRef';

/** How the value of a key is edited. */
export type KeyValueKind = 'number' | 'color' | 'flag' | 'inherit' | 'placeholder' | 'readonly';
/** One key as the form shows it (bone values absolute, scale bezier handles in channel units). */
export interface KeyDetail { ref: KeyRef; kind: KeyValueKind; value: unknown; curve: Curve | undefined }

type StoredKey = { f: number; v?: unknown; curve?: Curve | undefined };
const KINDS: Readonly<Record<string, KeyValueKind>> = { rgb: 'color', dark: 'color', inherit: 'inherit', attachment: 'placeholder', ikBend: 'flag', ikStretch: 'flag', ikCompress: 'flag' };
const isKeyChannel = (c: string): c is keyof typeof KEY_CHANNELS => Object.hasOwn(KEY_CHANNELS, c);

/** The stored key of `ref` (key channels, deform, draw order, event), or undefined. */
function storedKey(doc: Doc, ref: KeyRef): StoredKey | undefined {
  const anim = doc.animations[ref.animation];
  if (anim === undefined) return undefined;
  if (ref.channel === 'deform') return anim.deform[ref.target]?.find((k) => k.f === ref.frame);
  if (ref.channel === 'drawOrder') return anim.drawOrder.find((k) => k.f === ref.frame);
  if (ref.channel === 'event') return anim.events.find((k) => k.f === ref.frame && k.event === ref.target);
  if (!isKeyChannel(ref.channel)) return undefined;
  const spec = KEY_CHANNELS[ref.channel];
  const track = (anim[spec.group] as Readonly<Record<string, Readonly<Record<string, StoredKey[] | undefined>> | undefined>>)[ref.target];
  return track?.[spec.prop]?.find((k) => k.f === ref.frame);
}

/**
 * Value and curve as the key commands take them: a value-mode curve timeline is read through the curve editor's
 * display track (W3-43; bone transforms absolute, scale bezier `dv` in channel units); any other key as stored.
 */
function commandUnits(doc: Doc, ref: KeyRef, key: StoredKey): { value: unknown; curve: Curve | undefined } {
  const curveRef = toCurveRef(ref);
  const track = curveRef === null ? null : buildCurveTrack(doc, curveRef);
  const i = track === null ? -1 : track.frames.indexOf(ref.frame);
  if (track === null || i < 0) return { value: key.v, curve: key.curve };
  return { value: track.mode === 'value' ? track.values[i] : key.v, curve: track.curves[i] };
}

const curveId = (curve: Curve): string => JSON.stringify(curve);

/** Keyframe form model functions. */
export const keyDetails = {
  /** One key's editable form, or null when the key no longer exists. */
  detail(doc: Doc, ref: KeyRef): KeyDetail | null {
    const key = storedKey(doc, ref);
    if (key === undefined) return null;
    const kind: KeyValueKind = isKeyChannel(ref.channel) ? (KINDS[ref.channel] ?? 'number') : 'readonly';
    return { ref, kind, ...commandUnits(doc, ref, key) };
  },
  /** True for timelines that take curves: numeric key channels and deform. */
  curved(channel: string): boolean {
    return channel === 'deform' || (isKeyChannel(channel) && KEY_CHANNELS[channel].numeric);
  },
  /** The curve every existing selected key of a curved timeline carries ('linear' when none is set); null when they differ or there is none. */
  sharedCurve(doc: Doc, keys: readonly KeyRef[]): Curve | null {
    const curves = keys.filter((k) => keyDetails.curved(k.channel)).flatMap((k) => {
      const detail = keyDetails.detail(doc, k);
      return detail === null ? [] : [detail.curve ?? 'linear'];
    });
    const first = curves[0];
    return first !== undefined && curves.every((c) => curveId(c) === curveId(first)) ? first : null;
  },
};
